import React, { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Tabs } from "components/navigation/Tabs"

import { useTimezone } from "hooks/useTimezone"

import { settingsService } from "services/settings/settings.service"

import { formatUsd } from "../utils"

import { GGR_HISTORY_EVENT_LABELS, GGR_SCOPE_TABS } from "./labels"
import { LimitDiffCell } from "./LimitDiffCell"
import { GgrHistoryEventType, GgrHistoryItem, GgrLimitScope } from "./types"

const PAGE_SIZE = 20

export const GgrLimitHistoryPanel: React.FC = () => {
  const { formatDateTime } = useTimezone()
  const { t } = useTranslation("translation", { useSuspense: false })
  const [scope, setScope] = useState<GgrLimitScope>("CASINO")
  const [items, setItems] = useState<GgrHistoryItem[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [eventType, setEventType] = useState<GgrHistoryEventType | "">("LIMIT_CONFIG_CHANGED")

  const eventOptions = useMemo(
    () => [
      { value: "", label: t("ggr.allEvents") },
      ...Object.entries(GGR_HISTORY_EVENT_LABELS).map(([value, label]) => ({
        value,
        label
      }))
    ],
    []
  )

  const load = async (reset = false, nextEventType?: typeof eventType) => {
    setLoading(true)
    try {
      const effectiveEventType = typeof nextEventType !== "undefined" ? nextEventType : eventType
      const nextOffset = reset ? 0 : offset

      const fetcher = scope === "SPORTSBOOK" ? settingsService.getSportsbookGgrHistory : settingsService.getGgrHistory

      const res = await fetcher({
        limit: PAGE_SIZE,
        offset: nextOffset,
        eventType: effectiveEventType || undefined
      })

      if (!res) return

      setItems((prev) => (reset ? res.items : [...prev, ...res.items]))
      setTotal(res.total)
      setOffset(nextOffset + res.items.length)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(true)
  }, [scope])

  const handleScopeChange = (key: string) => {
    setScope(key as GgrLimitScope)
    setItems([])
    setTotal(0)
    setOffset(0)
    setEventType("LIMIT_CONFIG_CHANGED")
  }

  const handleChangeEventType = (v: string) => {
    const next = v as any as GgrHistoryEventType | ""
    setEventType(next)
    setOffset(0)
    setItems([])
    setTotal(0)
    load(true, next)
  }

  const canLoadMore = items.length < total

  const rows = useMemo(
    () =>
      items.map((item, idx) => {
        const prev = items.slice(idx + 1).find((p) => p.eventType === "LIMIT_CONFIG_CHANGED")
        const showDiff = item.eventType === "LIMIT_CONFIG_CHANGED" && !!prev
        const prevLimit = prev?.limit ?? null

        const cells: { key: string; className: string; content: React.ReactNode }[] = [
          { key: "date", className: "py-2 pr-3 font-mono", content: formatDateTime(item.createdAt) },
          { key: "event", className: "py-2 pr-3 font-semibold", content: GGR_HISTORY_EVENT_LABELS[item.eventType] },
          { key: "enabled", className: "py-2 pr-3", content: item.enabled ? "Yes" : "No" },
          {
            key: "limit",
            className: "py-2 pr-3",
            content: <LimitDiffCell limit={item.limit} prevLimit={prevLimit} showDiff={showDiff} />
          },
          {
            key: "snapshot",
            className: "py-2 pr-3",
            content: item.snapshotCurrent !== null ? `${formatUsd(item.snapshotCurrent)} USD` : "—"
          },
          {
            key: "reached",
            className: "py-2 pr-3 font-semibold",
            content: (
              <span className={item.isLimitReached ? "text-rose-500" : "text-emerald-500"}>{item.isLimitReached ? "Yes" : "No"}</span>
            )
          },
          { key: "period", className: "py-2 pr-3 font-mono", content: item.snapshotPeriod ?? "—" }
        ]

        return { id: item.id, cells }
      }),
    [items, formatDateTime]
  )

  return (
    <section className="mt-4 w-full border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] rounded-lg p-4 bg-white dark:bg-[#0C0C1A]">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div className="flex flex-col gap-2">
          <h3 className="text-xl font-semibold">{t("ggr.history")}</h3>

          <Tabs tabs={GGR_SCOPE_TABS} activeTab={scope} onChange={handleScopeChange} />

          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">{t("ggr.event")}</label>
            <select
              className="text-xs rounded border border-black/10 bg-white px-2 py-1 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
              value={eventType}
              onChange={(e) => handleChangeEventType(e.target.value)}
              disabled={loading}
            >
              {eventOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={() => load(true)}
          className="text-xs px-3 py-1 rounded border border-[rgba(169,78,206,0.8)] text-[rgba(169,78,206,1)] hover:bg-[rgba(169,78,206,0.08)] dark:text-white dark:border-[rgba(169,78,206,0.8)] disabled:opacity-60"
          disabled={loading}
          title="Reload history"
        >
          {loading ? t("common.loading") : t("common.refresh")}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="text-gray-500 dark:text-gray-400">
            <tr className="text-left">
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Event</th>
              <th className="py-2 pr-3">Enabled</th>
              <th className="py-2 pr-3">Limit</th>
              <th className="py-2 pr-3">Snapshot</th>
              <th className="py-2 pr-3">Reached</th>
              <th className="py-2 pr-3">Period</th>
            </tr>
          </thead>

          <tbody className="text-gray-900 dark:text-white">
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-4 text-gray-500 dark:text-gray-400">
                  {t("ggr.noHistory")}
                </td>
              </tr>
            ) : (
              rows.map(({ id, cells }) => (
                <tr key={id} className="border-t border-black/5 dark:border-white/10">
                  {cells.map((cell) => (
                    <td key={cell.key} className={cell.className}>
                      {cell.content}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{t("ggr.showing", { current: items.length, total })}</span>

        <button
          type="button"
          onClick={() => load(false)}
          className="px-3 py-1 rounded border border-black/10 hover:border-[rgba(169,78,206,0.6)] disabled:opacity-50 dark:border-white/10"
          disabled={loading || !canLoadMore}
        >
          {loading ? t("common.loading") : t("ggr.loadMore")}
        </button>
      </div>
    </section>
  )
}
